import { computed, Directive, HostBinding, input, signal } from '@angular/core';
import type { IconProps as CoreIconProps, IconData, IconNodes } from '@primeicons/core/types';
import { formatIconSize } from '@primeicons/core/utils';
import { cn } from '@primeuix/utils/classnames';

// Shared template for all icon components
export const ICON_TEMPLATE = `
        @for (node of iconNodes(); track node[1]['key'] || $index) {
            @switch (node[0]) {
                @case ('path') {
                    <svg:path
                        [attr.d]="node[1]['d']"
                        [attr.fill]="node[1]['fill']"
                        [attr.fill-opacity]="node[1]['fillOpacity']"
                        [attr.fill-rule]="node[1]['fillRule']"
                        [attr.clip-rule]="node[1]['clipRule']"
                        [attr.stroke]="node[1]['stroke']"
                        [attr.stroke-width]="node[1]['strokeWidth']"
                        [attr.stroke-opacity]="node[1]['strokeOpacity']"
                        [attr.opacity]="node[1]['opacity']"
                    />
                }
                @case ('circle') {
                    <svg:circle
                        [attr.cx]="node[1]['cx']"
                        [attr.cy]="node[1]['cy']"
                        [attr.r]="node[1]['r']"
                        [attr.fill]="node[1]['fill']"
                        [attr.fill-opacity]="node[1]['fillOpacity']"
                        [attr.opacity]="node[1]['opacity']"
                    />
                }
                @case ('rect') {
                    <svg:rect
                        [attr.x]="node[1]['x']"
                        [attr.y]="node[1]['y']"
                        [attr.width]="node[1]['width']"
                        [attr.height]="node[1]['height']"
                        [attr.rx]="node[1]['rx']"
                        [attr.ry]="node[1]['ry']"
                        [attr.fill]="node[1]['fill']"
                        [attr.fill-opacity]="node[1]['fillOpacity']"
                        [attr.opacity]="node[1]['opacity']"
                    />
                }
                @case ('line') {
                    <svg:line
                        [attr.x1]="node[1]['x1']"
                        [attr.y1]="node[1]['y1']"
                        [attr.x2]="node[1]['x2']"
                        [attr.y2]="node[1]['y2']"
                        [attr.stroke]="node[1]['stroke']"
                        [attr.stroke-opacity]="node[1]['strokeOpacity']"
                        [attr.opacity]="node[1]['opacity']"
                    />
                }
                @case ('polyline') {
                    <svg:polyline
                        [attr.points]="node[1]['points']"
                        [attr.fill]="node[1]['fill']"
                        [attr.fill-opacity]="node[1]['fillOpacity']"
                        [attr.opacity]="node[1]['opacity']"
                    />
                }
                @case ('polygon') {
                    <svg:polygon
                        [attr.points]="node[1]['points']"
                        [attr.fill]="node[1]['fill']"
                        [attr.fill-opacity]="node[1]['fillOpacity']"
                        [attr.opacity]="node[1]['opacity']"
                    />
                }
                @case ('ellipse') {
                    <svg:ellipse
                        [attr.cx]="node[1]['cx']"
                        [attr.cy]="node[1]['cy']"
                        [attr.rx]="node[1]['rx']"
                        [attr.ry]="node[1]['ry']"
                        [attr.fill]="node[1]['fill']"
                        [attr.fill-opacity]="node[1]['fillOpacity']"
                        [attr.opacity]="node[1]['opacity']"
                    />
                }
            }
        }
`;

@Directive()
export class CoreIcon {
    protected _iconSignal = signal<IconData | null>(null);

    get _icon(): IconData | null {
        return this._iconSignal();
    }
    set _icon(value: IconData | null) {
        this._iconSignal.set(value);
    }

    size = input<number | string | undefined>(undefined);
    color = input<string | undefined>(undefined);
    styleClass = input<string | undefined>(undefined);
    spin = input<boolean | undefined>(undefined);

    iconNodes = computed<IconNodes>(() => this._iconSignal()?.nodes ?? []);

    computedSize = computed(() => this.size() ?? 20);
    computedClass = computed(() => {
        const icon = this._iconSignal();

        return cn('p-icon', icon?.name && `p-icon-${icon.name}`, this.spin() && 'p-icon-spin', this.styleClass());
    });

    @HostBinding('attr.width') get hostWidth() {
        return this.computedSize();
    }
    @HostBinding('attr.height') get hostHeight() {
        return this.computedSize();
    }
    @HostBinding('attr.viewBox') get hostViewBox() {
        return this._iconSignal()?.svg?.viewBox;
    }
    @HostBinding('attr.fill') get hostFill() {
        return this._iconSignal()?.svg?.fill;
    }
    @HostBinding('attr.xmlns') get hostXmlns() {
        return this._iconSignal()?.svg?.xmlns;
    }
    @HostBinding('attr.aria-hidden') hostAriaHidden = 'true';
    @HostBinding('class') get hostClass() {
        return this.computedClass();
    }
    @HostBinding('style.color') get hostColor() {
        return this.color() || null;
    }
    @HostBinding('style.--px-icon-size') get hostIconSize() {
        return formatIconSize(this.size()) ?? null;
    }
}

export type { CoreIconProps, IconData };
